import { z } from 'zod';

/** Shown for both a blank and a cleared registration date. */
export const REGISTRATION_DATE_REQUIRED = 'First registered date is required.';

// An Invalid Date from an unparseable stored string is truthy, so it needs its own predicate and copy
// (see RegistrationDateField.tsx).
export const REGISTRATION_DATE_UNREADABLE =
    'First registered date could not be read. Please pick it again.';

// z.date() cannot override the invalid_date message, so this copy is applied by INVALID_DATE_COPY
// (additionalVehicleDetailsFormState.ts), keyed on the invalid_date issue code.
export const LAST_MOT_DATE_UNREADABLE = 'Last MOT date could not be read. Please pick it again.';
export const LAST_SERVICE_DATE_UNREADABLE =
    'Last service date could not be read. Please pick it again.';

// Last Service Mileage is both-or-neither with Last Service Date and bounded by current mileage; the date may not be future.
export const LAST_SERVICE_MILEAGE_REQUIRED =
    'Last service mileage is required when a last service date is given.';
export const LAST_SERVICE_DATE_REQUIRED_FOR_MILEAGE =
    'Last service date is required when a last service mileage is given.';
export const LAST_SERVICE_MILEAGE_EXCEEDS_CURRENT =
    'Last service mileage cannot exceed current mileage.';
export const LAST_SERVICE_DATE_IN_FUTURE = 'Last service date cannot be in the future.';

// `vrm` 2-7 mirrors this app's own search gate (`validateVRM` in vinVrmValidation.ts).
export const VRM_LENGTH_MESSAGE = 'Registration must be between 2 and 7 characters.';
// Matches the write contract's exact length and charset (StoreQuoteRequest.php:214).
export const VIN_LENGTH_MESSAGE = 'VIN must be 17 characters, using A-Z and 0-9 but not I, O or Q.';

// Mirrors VehicleIdentifier::vrm() in vehicles-service (upper-case, spaces stripped); change the two together.
// Empty yields undefined, not '', so .optional() stays reachable ('' fails min(2)); do not simplify.
export const normaliseVrm = (v?: string | null): string | undefined => {
    if (!v) return undefined;
    const n = v.toUpperCase().replace(/\s/g, '');
    return n === '' ? undefined : n;
};

// Canonicalises as VehicleIdentifier::vin() does: upper-case, outer trim only.
// An empty result returns undefined, keeping .optional() reachable.
export const normaliseVin = (v?: string | null): string | undefined => {
    if (!v) return undefined;
    const n = v.toUpperCase().trim();
    return n === '' ? undefined : n;
};

const additionalVehicleDetailsObjectSchema = z.object({
    // Exact length and charset, mirroring the write contract this value is saved through.
    vin: z.string().regex(/^[A-HJ-NPR-Z0-9]{17}$/, VIN_LENGTH_MESSAGE).optional(),
    // min(2) matches the app's search gate (validateVRM); max(7) is deliberately unchanged, as widening it
    // would let a genuinely out-of-range value flow through.
    vrm: z.string().min(2, VRM_LENGTH_MESSAGE).max(7, VRM_LENGTH_MESSAGE).optional(),
    mileage: z.number().min(0).nullable().optional(),
    annualMileage: z.number().min(0).nullable().optional(),
    // Required: the wire field is mandatory, and optional meant an opaque 422 or a fabricated today. Both messages are set
    // because DatePicker emits null on clear, which z.date() reports as invalid_type. vin/vrm stay optional deliberately.
    registrationDate: z.date({
        required_error: REGISTRATION_DATE_REQUIRED,
        invalid_type_error: REGISTRATION_DATE_REQUIRED,
    }),
    // DatePicker emits null on clear and z.date().optional() rejects it; the fix is the Controller's date ?? undefined,
    // not .nullable(), which would widen the exported input type and reach QuoteContext.
    lastMotDate: z.date().optional(),
    lastServiceDate: z.date().optional(),
    // undefined means absent. Its estimate is offered, never seeded, so the mileage/annualMileage
    // null sentinel does not apply.
    lastServiceMileage: z.number().min(0).optional(),
});

// new Date() at parse time, not module load, so the local calendar day matches toDateKey and flatpickr's local midnight.
const endOfToday = (): number => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date.getTime();
};

export const additionalVehicleDetailsSchema = additionalVehicleDetailsObjectSchema.superRefine(
    (values, ctx) => {
        const { lastServiceDate, lastServiceMileage, mileage } = values;

        if (lastServiceDate && lastServiceMileage === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastServiceMileage'],
                message: LAST_SERVICE_MILEAGE_REQUIRED,
            });
        }
        if (!lastServiceDate && lastServiceMileage !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastServiceDate'],
                message: LAST_SERVICE_DATE_REQUIRED_FOR_MILEAGE,
            });
        }
        if (typeof mileage === 'number' && lastServiceMileage !== undefined && lastServiceMileage > mileage) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastServiceMileage'],
                message: LAST_SERVICE_MILEAGE_EXCEEDS_CURRENT,
            });
        }
        if (lastServiceDate && lastServiceDate.getTime() > endOfToday()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastServiceDate'],
                message: LAST_SERVICE_DATE_IN_FUTURE,
            });
        }
    },
);

export type AdditionalVehicleDetailsFormInputs = z.infer<typeof additionalVehicleDetailsSchema>;
